import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../core/data/redux/store';
import { createParent, updateParent } from '../core/data/redux/employee/employeeAction'; // assuming this is the correct import
import { message } from 'antd';
import dayjs from 'dayjs';

interface ParentFormValues {
    fatherName: string;
    motherName: string;
    fatherPhone: string;
    motherPhone: string;
    fatherEmail: string;
    motherEmail: string;
    fatherDateOfBirth: Date | null;
    motherDateOfBirth: Date | null;
    fatherOccupation: string;
    motherOccupation: string;
    address: string;
    parentId?: string;
}

export const useGuardianForm = () => {

    const [parentData, setParentData] = useState<ParentFormValues>({
        fatherName: '',
        motherName: '',
        fatherPhone: '',
        motherPhone: '',
        fatherEmail: '',
        motherEmail: '',
        fatherDateOfBirth: null,
        motherDateOfBirth: null,
        fatherOccupation: '',
        motherOccupation: '',
        address: '',
    });

    const [parentErrors, setParentErrors] = useState<{ [key: string]: string }>({});
    const { employee } = useSelector((state: any) => state.employee);
    const { createParentError, createParentSuccess } = useSelector((state: any) => state.employee);
    const { user } = useSelector((state: any) => state.auth);
    const { employeeFullDetails } = useSelector((state: any) => state.employee);
    const parentId = employeeFullDetails?.parents[0]?.Id;


    useEffect(() => {
        if (employeeFullDetails && employeeFullDetails.parents.length > 0) {
            const parents = employeeFullDetails.parents[0];
            setParentData({
                fatherName: parents.FathersName || '',
                motherName: parents.MothersName || '',
                fatherPhone: parents.FathersPhoneNumber || '',
                motherPhone: parents.MothersPhoneNumber || '',
                fatherEmail: parents.FathersEmail || '',
                motherEmail: parents.MothersEmail || '',
                fatherDateOfBirth: parents.FathersDateOfBirth ? new Date(parents.FathersDateOfBirth) : null,
                motherDateOfBirth: parents.MothersDateOfBirth ? new Date(parents.MothersDateOfBirth) : null,
                fatherOccupation: parents.FathersOccupation || '',
                motherOccupation: parents.MothersOccupation || '',
                address: parents.Address || '',
            });
        }
    }, [employeeFullDetails]);

    const dispatch = useDispatch<AppDispatch>();

    const handleParentChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setParentData((prevData) => ({ ...prevData, [name]: value }));
    };

    const resetParentForm = () => {
        setParentData({
            fatherName: '',
            motherName: '',
            fatherPhone: '',
            motherPhone: '',
            fatherEmail: '',
            motherEmail: '',
            fatherDateOfBirth: null,
            motherDateOfBirth: null,
            fatherOccupation: '',
            motherOccupation: '',
            address: '',
        });
        setParentErrors({});
    };

    const isValidPhoneNumber = (number: any) => {
        // Must be 10 digits
        if (!/^\d{10}$/.test(number)) return false;//10
        if (/^(\d)\1{9}$/.test(number)) return false;//1111
        if (/^(\d{2})\1{4}$/.test(number)) return false;//2323
        if (/^[01]/.test(number)) return false;//!start 0, 1
        const invalidList = ["1234567890", "9876543210"];
        if (invalidList.includes(number)) return false;
        return true;
    };

    const validateParent = (): Promise<{ isValid: boolean; missingFields?: string[] }> => {
        let tempErrors: { [key: string]: string } = {};
        let isValid = true;
        const missingFields: string[] = [];

        if (!parentData.fatherName?.trim()) {
            tempErrors.fatherName = "Father's name is required";
            missingFields.push("Father's Name");
            isValid = false;
        } else if (!/^[a-zA-Z\s.-]+$/.test(parentData.fatherName.trim())) {
            tempErrors.fatherName = "Only letters, spaces, dots, and hyphens are allowed";
            isValid = false;
        }

        if (!parentData.motherName?.trim()) {
            tempErrors.motherName = "Mother's name is required";
            missingFields.push("Mother's Name");
            isValid = false;
        } else if (!/^[a-zA-Z\s.-]+$/.test(parentData.motherName.trim())) {
            tempErrors.motherName = "Only letters, spaces, dots, and hyphens are allowed";
            isValid = false;
        }

        if (parentData.fatherPhone) {
            if (!isValidPhoneNumber(parentData.fatherPhone)) {
                tempErrors.fatherPhone = "Please enter a realistic 10-digit phone number for Father";
                missingFields.push("Father's Phone");
                isValid = false;
            }
        }
        if (parentData.motherPhone) {
            if (!isValidPhoneNumber(parentData.motherPhone)) {
                tempErrors.motherPhone = "Please enter a realistic 10-digit phone number for Mother";
                missingFields.push("Mother's Phone");
                isValid = false;
            }
        }
        if (parentData.address && parentData.address.length > 499) {
            tempErrors.address = 'Max 499 characters allowed';
            isValid = false;
        }
        if (parentData.fatherEmail && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(parentData.fatherEmail)) {
            tempErrors.fatherEmail = 'Email is not valid';
            missingFields.push('Father Email');
            isValid = false;
        }
        if (parentData.motherEmail && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(parentData.motherEmail)) {
            tempErrors.motherEmail = 'Email is not valid';
            missingFields.push('Mother Email');
            isValid = false;
        }

        setParentErrors(tempErrors);
        return Promise.resolve({ isValid, missingFields });
    };


    const handleParentSubmit = (e: React.FormEvent, clearForm: boolean = false, showMessage: boolean = true): Promise<boolean> => {
        return new Promise((resolve) => {
            e.preventDefault();

            if (!parentData.fatherName || !parentData.motherName) {
                if (showMessage) message.warning('Please fill all fields to add/update a Parent');
                resolve(false);
                return;
            }
            if (parentData.address.length > 499) {
                message.warning('Parent Address should be less than 499 characters');
                resolve(false);
                return;
            }

            validateParent().then((validationResult) => {
                if (!validationResult.isValid) {
                    resolve(false);
                    return;
                }

                const parentPayload = {
                    FathersName: parentData.fatherName,
                    MothersName: parentData.motherName,
                    FathersPhoneNumber: parentData.fatherPhone,
                    MothersPhoneNumber: parentData.motherPhone,
                    FathersEmail: parentData.fatherEmail,
                    MothersEmail: parentData.motherEmail,
                    FathersDateOfBirth: parentData.fatherDateOfBirth
                        ? dayjs(parentData.fatherDateOfBirth).format('YYYY-MM-DD')
                        : null,
                    MothersDateOfBirth: parentData.motherDateOfBirth
                        ? dayjs(parentData.motherDateOfBirth).format('YYYY-MM-DD')
                        : null,
                    FathersOccupation: parentData.fatherOccupation,
                    MothersOccupation: parentData.motherOccupation,
                    Address: parentData.address,
                    CreatedBy: user?.UserId,
                    ModifiedBy: user?.UserId,
                };

                if (parentId) {
                    // Parent exists, so update
                    const result = dispatch(updateParent({ Id: parentId, parentData: parentPayload }));
                    result.then((response: any) => {
                        if (response.meta.requestStatus === 'fulfilled') {
                            if (showMessage) message.success('Parents updated successfully');
                            if (clearForm) resetParentForm();
                            resolve(true);
                        } else {
                            if (showMessage) message.warning('Something went wrong while updating');
                            resolve(false);
                        }
                    }).catch((error: any) => {
                        if (showMessage) message.error('Failed to update parents');
                        resolve(false);
                    });
                } else {
                    // Parent does not exist, so create
                    const employeeIdToUse =
                        employeeFullDetails?.employee?.[0]?.EmployeeID || employee?.EmployeeId;

                    const result = dispatch(
                        createParent({ employeeId: employeeIdToUse, parentData: parentPayload })
                    );

                    result
                        .then((response: any) => {
                            if (response.meta.requestStatus === 'fulfilled') {
                                message.success('Parents saved successfully');
                                if (clearForm) resetParentForm();
                                resolve(true);
                            } else if (response.meta.requestStatus === 'rejected') {
                                message.warning(response.payload);
                                resolve(false);
                            } else {
                                console.log(response);
                                resolve(false);
                                message.error('Something went wrong');
                            }
                        })
                        .catch((error: any) => {
                            console.log(error);
                            resolve(false);
                            message.error('Failed to save parents');
                        });
                }
            })
        });
    };

    useEffect(() => {
        if (createParentSuccess) {
            // alert('Parent information created successfully');
            // setParentData({
            //     fatherName: '',
            //     motherName: '',
            //     fatherPhone: '',
            //     motherPhone: '',
            //     fatherEmail: '',
            //     motherEmail: '',
            //     fatherDateOfBirth: new Date(),
            //     motherDateOfBirth: new Date(),
            //     fatherOccupation: '',
            //     motherOccupation: '',
            //     address: '',
            // });
            // setParentErrors({});
        } else if (createParentError) {
            alert(createParentError);
        }
    }, [createParentSuccess, createParentError]);

    return {
        parentData,
        setParentData,
        handleParentChange,
        handleParentSubmit,
        parentErrors,
        resetParentForm,
        validateParent
    };
};
